import {
  CMP_EXPORT_DESTINATION,
  CMP_EXPORT_SCHEMA_VERSION,
  CmpExportFile,
  CmpExportItem,
  CmpExportMediaEntry,
  CmpExportMetadata,
} from '@/declarations/models/ContentMigrationImport';

/**
 * Reads an export file in the browser and says what importing it would mean, so the
 * operator sees it *before* anything is uploaded. Nothing here contacts the server.
 *
 * Deliberately tolerant about the file's shape and strict about only two things - the
 * schema version and the destination - because those are the two that mean "this is
 * not a file for this endpoint". Everything else is reported, not refused: a file with
 * media the platform could not resolve is still importable, it just produces pages
 * with missing images, and that is the operator's call to make with the number in
 * front of them.
 *
 * Every count comes with the rows behind it (`items`, `media.files`,
 * `media.unresolvedEntries`), because a count alone cannot be checked: "1 annen fil"
 * is a question, "BR20182.pdf (application/pdf)" is an answer. The file is already
 * parsed in memory here, so carrying the detail costs nothing and saves the operator
 * opening a multi-megabyte JSON file by hand to find out what one odd number was.
 *
 * **Media is counted over the file's whole library, not over the pages' references.**
 * The envelope carries a `media` list of every file the source has in EMS, and most
 * of it is used by some page while some of it is used by none - 84 of
 * www.norskolje.museum.no's 687 files. Both belong on the site: Museum24's media
 * gallery lists what it has rows for, so a file nobody mentioned to it is a file an
 * editor cannot pick. One list is therefore built from the union of the library and
 * the items' references, and each file says how many pages use it.
 */

/** Media kinds, in the order the detail view lists them. */
export const MEDIA_KINDS = ['image', 'video', 'audio', 'other'] as const;

export type CmpMediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_KIND_LABELS: Record<CmpMediaKind, string> = {
  image: 'Bilder',
  video: 'Videoer',
  audio: 'Lyd',
  other: 'Andre filer',
};

/** `canonical_data.kind` as the platform writes it, in Norwegian - singular for a row,
 *  plural for a group heading. An unknown kind is shown raw rather than hidden: a new
 *  kind on the platform side is information, not an error. */
const PAGE_KIND_LABELS: Record<string, { one: string; many: string }> = {
  article: { one: 'Artikkel', many: 'Artikler' },
  landing_page: { one: 'Landingsside', many: 'Landingssider' },
};

export function pageKindLabel(kind: string, count = 1): string {
  const labels = PAGE_KIND_LABELS[kind];
  if (!labels) return kind || 'Ukjent type';
  return count === 1 ? labels.one : labels.many;
}

/** One distinct EMS file the import would give this site. */
export interface CmpMediaFile {
  dmsId: string;
  kind: CmpMediaKind;
  filename: string;
  mimetype: string;
  /** How many of the file's items reference it. 0 means no page uses it - which is
   * not a problem: it is a file the museum uploaded and should be able to pick. */
  references: number;
  /** Listed in the envelope's own `media` list, as opposed to only being referenced
   * by an item. Everything the platform exports now is. */
  inLibrary: boolean;
  /** The asset's title, where the library entry carried one. */
  title: string;
}

/** One media reference the platform could not resolve to an EMS id. */
export interface CmpUnresolvedMedia {
  /** An unresolved reference has no EMS id and no identity of its own - two of them on
   * one page can name the same file - so it is numbered in the order it was read. */
  id: string;
  sourceObjectId?: number;
  pageTitle: string;
  filename: string;
  reason: string;
}

/** One page the import would create, as the file describes it. */
export interface CmpPageDetail {
  sourceObjectId?: number;
  title: string;
  /** Raw platform value - `article`, `landing_page`, ... See `pageKindLabel`. */
  kind: string;
  slug: string;
  sourceUrl: string;
  /** Media references on this item that carry an EMS id. */
  resolvedMedia: number;
  /** Media references on this item with no EMS id - images this page will lack. */
  unresolvedMedia: number;
  /** M24 page locale the item would be filed under, or UNSTATED_LANGUAGE. */
  locale: string;
  /** The path M24 would give the page: the last segment of the source URL, after the
   * same locale and date prefixes the backend strips. */
  path: string;
  /** The path of the page this one sits under in the source, when the URL has one. */
  parentPath?: string;
  /** 1 for a root page, 2 for a child, and so on. */
  depth: number;
}

/** What the source URLs say about how the pages sit under each other. */
export interface CmpHierarchySummary {
  /** Pages whose URL puts them under another page. 0 means the file is flat and there
   * is no structure to choose about. */
  nested: number;
  /** Pages at the top level of the source. */
  root: number;
  maxDepth: number;
  /** Distinct parents the nested pages name. */
  parents: number;
  /** Parents that are themselves pages in this file, so a structured import can attach
   * their children to them. */
  parentsInFile: number;
  /** Parents no page in this file has. A child of one is attached to an existing page at
   * that path on the site, or lands at root - M24 never invents the parent. */
  parentsMissing: Array<string>;
}

export interface CmpMediaBreakdown {
  /** Media references across all items, counting an item's own references once. */
  references: number;
  /** Distinct EMS ids - the number of actual files, since several pages routinely
   * share one image. */
  uniqueFiles: number;
  images: number;
  videos: number;
  audio: number;
  otherFiles: number;
  /** References the platform could not resolve to an EMS id. Importing these produces
   * pages whose images are missing, so this is the number that should stop an upload. */
  unresolved: number;
  /** Files some page in this file uses. */
  used: number;
  /** Files no page in this file uses - the ones that were missing from the gallery
   * before the export started carrying the library. */
  unused: number;
  /** Entries in the envelope's own `media` list. 0 means a file written before the
   * platform sent the library at all. */
  library: number;
  /** Every distinct file, kind by kind - the detail behind the counts above. */
  files: Array<CmpMediaFile>;
  /** Every unresolved reference, so `unresolved` can say which files are missing. */
  unresolvedEntries: Array<CmpUnresolvedMedia>;
}

/** M24 page locale -> Norwegian name, for the languages the import mapper knows. An
 *  unknown code is shown raw. */
const LANGUAGE_LABELS: Record<string, string> = {
  no: 'Norsk',
  nn: 'Nynorsk',
  en: 'Engelsk',
  de: 'Tysk',
  sv: 'Svensk',
};

/** Items whose language the file does not state. M24 resolves those from the URL and
 *  the source's configured default, so the browser cannot say which language they
 *  are - only that they are not a *new* one. */
export const UNSTATED_LANGUAGE = 'x-default';

export function languageLabel(locale: string): string {
  if (locale === UNSTATED_LANGUAGE) return 'Nettstedets standardspråk';
  return LANGUAGE_LABELS[locale] ?? locale;
}

/** One language the file's pages are in, as M24 would file it. */
export interface CmpLanguageCount {
  /** M24 page locale: the tag's primary subtag, with `nb` folded into `no`. */
  locale: string;
  count: number;
}

export interface CmpExportSummary {
  pages: number;
  /** Pages per language, most first. The same folding the backend mapper applies
   * (`nb-NO` -> `no`, `de-DE` -> `de`), so this can be compared with the site's
   * configured languages. */
  languages: Array<CmpLanguageCount>;
  /** Pages per `kind`, commonest first, so "472 sider" can say how they split. */
  pagesByKind: Array<{ kind: string; count: number }>;
  /** One row per item, in file order - which is parents before children, the order the
   * import depends on. */
  items: Array<CmpPageDetail>;
  media: CmpMediaBreakdown;
  hierarchy: CmpHierarchySummary;
  metadata: CmpExportMetadata;
  /** The environment the file's EMS ids come from, from the envelope or its metadata. */
  dmsEnvironment?: string;
  /** Reasons this file should not be imported at all. Non-empty means refuse. */
  problems: Array<string>;
  /** Things worth seeing before importing, but not reasons to refuse. */
  warnings: Array<string>;
}

/**
 * Classified on `mimetype`, not on `media_kind`: the latter is EMS's own split and is
 * only ever `image` or `file`, so a video and a PDF look identical through it.
 * `media_kind` is the fallback for an entry that somehow has no mimetype.
 */
function classify(mimetype?: string | null, mediaKind?: string | null): CmpMediaKind {
  if (typeof mimetype === 'string' && mimetype.includes('/')) {
    const type = mimetype.split('/')[0].toLowerCase();
    if (type === 'image' || type === 'video' || type === 'audio') return type;
    return 'other';
  }
  return mediaKind === 'image' ? 'image' : 'other';
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The file facts an entry carries, however it reached us. */
function describe(entry: CmpExportMediaEntry): Omit<CmpMediaFile, 'references' | 'inLibrary'> {
  return {
    dmsId: entry.dms_id as string,
    kind: classify(entry.mimetype, entry.media_kind),
    filename: asText(entry.filename) || '(uten filnavn)',
    mimetype: asText(entry.mimetype),
    title: asText(entry.metadata?.title),
  };
}

/** A page with no title still has to be identifiable in the list, so it falls back to
 *  the id the import reports it under. */
function titleOf(item: CmpExportItem, index: number): string {
  const title = asText(item.canonical_data?.title).trim();
  if (title) return title;
  const id = item.source_object_id ?? index + 1;
  return `(uten tittel) #${id}`;
}

interface Inspection {
  items: Array<CmpPageDetail>;
  pagesByKind: Array<{ kind: string; count: number }>;
  languages: Array<CmpLanguageCount>;
  media: CmpMediaBreakdown;
  hierarchy: CmpHierarchySummary;
}

/** `nb`, `en-us`: two letters with an optional region, and a language the mapper knows.
 *  The same rule as the backend's `_is_locale_segment`, for the same reason: `en` and
 *  `de` are ordinary Norwegian words, so a prefix match would eat real slugs. */
const LOCALE_SEGMENT = /^[a-z]{2}(-[a-z]{2,3})?$/i;
const KNOWN_PRIMARY_SUBTAGS = new Set(['no', 'nb', 'nn', 'en', 'de', 'sv']);

function isLocaleSegment(segment: string): boolean {
  return LOCALE_SEGMENT.test(segment) && KNOWN_PRIMARY_SUBTAGS.has(segment.split('-')[0].toLowerCase());
}

/**
 * The path segments M24 derives a page's path and parent from - a mirror of the backend
 * mapper's `derive_path`, so the browser can say beforehand which pages the source
 * nests. Only the structural part is mirrored (prefix stripping, what counts as a
 * segment); the per-segment spelling rules are the backend's business, since nothing
 * here is compared against the site.
 */
export function sourcePathSegments(sourceUrl: string, slug?: string): Array<string> {
  let path = '';
  try {
    path = new URL(sourceUrl).pathname;
  } catch {
    path = sourceUrl.replace(/^[a-z]+:\/\/[^/]*/i, '');
  }
  let segments = path
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment).trim().toLowerCase();
      } catch {
        return segment.trim().toLowerCase();
      }
    })
    .filter(Boolean);
  if (segments.length > 0 && isLocaleSegment(segments[0])) segments = segments.slice(1);
  // A WordPress date permalink (`/2020/01/20/slug`) is a flat post, not three levels.
  if (segments.length >= 4 && /^\d{4}$/.test(segments[0]) && /^\d+$/.test(segments[1]) && /^\d+$/.test(segments[2])) {
    segments = segments.slice(3);
  }
  if (segments.length === 0 && slug) {
    const own = slug
      .replace(/^\/+|\/+$/g, '')
      .split('/')
      .pop()
      ?.trim()
      .toLowerCase();
    if (own) segments = [own];
  }
  return segments;
}

/** The structure the items' URLs describe, once every item's segments are known. */
function hierarchyOf(details: Array<CmpPageDetail>): CmpHierarchySummary {
  const paths = new Set(details.map((item) => item.path).filter(Boolean));
  const parents = new Set<string>();
  let nested = 0;
  let maxDepth = 0;
  details.forEach((item) => {
    maxDepth = Math.max(maxDepth, item.depth);
    if (item.parentPath) {
      nested += 1;
      parents.add(item.parentPath);
    }
  });
  const parentsMissing = Array.from(parents)
    .filter((parent) => !paths.has(parent))
    .sort((a, b) => a.localeCompare(b, 'nb'));
  return {
    nested,
    root: details.length - nested,
    maxDepth,
    parents: parents.size,
    parentsInFile: parents.size - parentsMissing.length,
    parentsMissing,
  };
}

/** The M24 locale a stated language tag maps to - mirrors the backend mapper's
 *  primary-subtag rule. */
function localeOf(tag: unknown): string {
  if (typeof tag !== 'string' || !tag.trim() || tag === UNSTATED_LANGUAGE) return UNSTATED_LANGUAGE;
  const primary = tag.split('-')[0].toLowerCase();
  return primary === 'nb' ? 'no' : primary;
}

/** Whether the site has a language version pages in `locale` can be filed under. The
 *  unstated bucket counts as supported: it resolves server-side to a language the
 *  site is assumed to have. */
function isSupported(locale: string, siteLocales: Array<string>): boolean {
  if (locale === UNSTATED_LANGUAGE) return true;
  return siteLocales.some((configured) => configured.toLowerCase() === locale);
}

/** One language in the file, judged against the site - what the review lists. */
export interface CmpLanguageReview extends CmpLanguageCount {
  label: string;
  /** False means the site has no version of this language: its pages are refused by
   * the import until the language is created under language settings. */
  supported: boolean;
}

/**
 * The file's languages against this site's, supported first. Pages in an unsupported
 * language are refused by the import (they would be invisible in the admin until the
 * language exists), so the operator sees that before uploading.
 */
export function languageReview(
  languages: Array<CmpLanguageCount>,
  siteLocales: Array<string>,
): Array<CmpLanguageReview> {
  return languages
    .map((language) => ({
      ...language,
      label: languageLabel(language.locale),
      supported: isSupported(language.locale, siteLocales),
    }))
    .sort((a, b) => Number(b.supported) - Number(a.supported) || b.count - a.count);
}

/** How many of the file's pages the import will take: everything in a supported language. */
export function importablePages(review: Array<CmpLanguageReview>): number {
  return review.filter((language) => language.supported).reduce((sum, language) => sum + language.count, 0);
}

/** "tysk og svensk" / "tysk, svensk og fransk" - a Norwegian list of language names. */
export function joinLanguageNames(labels: Array<string>): string {
  const names = labels.map((label) => label.toLowerCase());
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
}

/** One language in the file after the import ran. */
export interface CmpLanguageOutcome extends CmpLanguageReview {
  /** Pages M24 now has: created by this run, or already there unchanged. */
  imported: number;
  /** Pages it does not: rejected, skipped, or never attempted. */
  notImported: number;
}

/**
 * The import's result per language, joined from the file (which knows each page's
 * language) and the response (which knows each page's outcome). A supported language
 * can still have pages that were not imported - a path collision, an unmappable item -
 * which is why the count is "imported of total" rather than a yes/no.
 */
export function languageOutcome(
  items: Array<CmpPageDetail>,
  results: Array<{ source_object_id: number; status: string }>,
  siteLocales: Array<string>,
): Array<CmpLanguageOutcome> {
  const status = new Map(results.map((result) => [result.source_object_id, result.status]));
  const perLocale = new Map<string, { count: number; imported: number }>();
  items.forEach((item) => {
    const entry = perLocale.get(item.locale) ?? { count: 0, imported: 0 };
    entry.count += 1;
    const outcome = item.sourceObjectId === undefined ? undefined : status.get(item.sourceObjectId);
    if (outcome === 'stored' || outcome === 'unchanged') entry.imported += 1;
    perLocale.set(item.locale, entry);
  });
  const counts = Array.from(perLocale.entries()).map(([locale, { count }]) => ({ locale, count }));
  return languageReview(counts, siteLocales).map((language) => {
    const imported = perLocale.get(language.locale)?.imported ?? 0;
    return { ...language, imported, notImported: language.count - imported };
  });
}

/**
 * One pass over the library and the items, producing both the counts and the rows
 * behind them.
 *
 * Files are counted per distinct EMS id, because several pages routinely reference the
 * same image and "42 bilder" should mean 42 files, not 42 mentions - while each item's
 * own row counts references, which is what that page actually shows.
 *
 * The library is read first so that a file no page uses still gets a row, and so that
 * the library's own filename and metadata win over what a reference repeats.
 */
function inspect(items: Array<CmpExportItem>, library: Array<CmpExportMediaEntry>): Inspection {
  const seen = new Map<string, CmpMediaFile>();
  library.forEach((rawEntry) => {
    if (typeof rawEntry !== 'object' || rawEntry === null) return;
    const entry = rawEntry as CmpExportMediaEntry;
    if (!entry.dms_id || seen.has(entry.dms_id)) return;
    seen.set(entry.dms_id, { ...describe(entry), references: 0, inLibrary: true });
  });
  const details: Array<CmpPageDetail> = [];
  const kinds = new Map<string, number>();
  const languages = new Map<string, number>();
  let references = 0;
  const unresolvedEntries: Array<CmpUnresolvedMedia> = [];

  items.forEach((rawItem, index) => {
    const item = (typeof rawItem === 'object' && rawItem !== null ? rawItem : {}) as CmpExportItem;
    const title = titleOf(item, index);
    const entries = Array.isArray(item.media) ? item.media : [];
    let resolvedMedia = 0;
    let unresolvedMedia = 0;

    entries.forEach((rawEntry) => {
      if (typeof rawEntry !== 'object' || rawEntry === null) return;
      const entry = rawEntry as CmpExportMediaEntry;
      references += 1;
      if (!entry.dms_id) {
        unresolvedMedia += 1;
        unresolvedEntries.push({
          id: `${index}:${unresolvedMedia}`,
          sourceObjectId: item.source_object_id,
          pageTitle: title,
          filename: asText(entry.filename) || '(ukjent fil)',
          reason: asText(entry.unresolved_reason) || 'ukjent årsak',
        });
        return;
      }
      resolvedMedia += 1;
      const known = seen.get(entry.dms_id);
      if (known) {
        known.references += 1;
        return;
      }
      // Referenced but not in the library: a file written before the platform sent
      // one. Counted all the same - it is still media this import brings in.
      seen.set(entry.dms_id, { ...describe(entry), references: 1, inLibrary: false });
    });

    const kind = asText(item.canonical_data?.kind);
    kinds.set(kind, (kinds.get(kind) ?? 0) + 1);
    const locale = localeOf(item.canonical_data?.language?.tag);
    languages.set(locale, (languages.get(locale) ?? 0) + 1);
    const slug = asText(item.canonical_data?.slug);
    const sourceUrl = asText(item.canonical_data?.source_url);
    const segments = sourcePathSegments(sourceUrl, slug);
    details.push({
      sourceObjectId: item.source_object_id,
      title,
      kind,
      locale,
      slug,
      sourceUrl,
      resolvedMedia,
      unresolvedMedia,
      path: segments[segments.length - 1] ?? '',
      parentPath: segments.length > 1 ? segments[segments.length - 2] : undefined,
      depth: Math.max(1, segments.length),
    });
  });

  const files = Array.from(seen.values()).sort((a, b) =>
    a.kind === b.kind
      ? a.filename.localeCompare(b.filename, 'nb')
      : MEDIA_KINDS.indexOf(a.kind) - MEDIA_KINDS.indexOf(b.kind),
  );
  const countOf = (kind: CmpMediaKind) => files.filter((file) => file.kind === kind).length;
  const used = files.filter((file) => file.references > 0).length;

  return {
    items: details,
    pagesByKind: Array.from(kinds.entries())
      .map(([kind, count]) => ({ kind, count }))
      .sort((a, b) => b.count - a.count),
    languages: Array.from(languages.entries())
      .map(([locale, count]) => ({ locale, count }))
      .sort((a, b) => b.count - a.count),
    media: {
      references,
      uniqueFiles: files.length,
      images: countOf('image'),
      videos: countOf('video'),
      audio: countOf('audio'),
      otherFiles: countOf('other'),
      unresolved: unresolvedEntries.length,
      used,
      unused: files.length - used,
      library: files.filter((file) => file.inLibrary).length,
      files,
      unresolvedEntries,
    },
    hierarchy: hierarchyOf(details),
  };
}

export class CmpExportParseError extends Error {}

/**
 * Parse the text of an export file. Throws `CmpExportParseError` when it is not JSON
 * at all - everything that *is* JSON comes back as a summary, with `problems` saying
 * whether it may be imported.
 */
export function summariseCmpExport(text: string): CmpExportSummary {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new CmpExportParseError('Filen er ikke gyldig JSON. Forventet en eksportfil fra plattformen.');
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new CmpExportParseError('Filen inneholder ikke et JSON-objekt. Forventet en eksportfil fra plattformen.');
  }

  const file = parsed as CmpExportFile;
  const problems: Array<string> = [];
  const warnings: Array<string> = [];

  if (file.schema_version !== CMP_EXPORT_SCHEMA_VERSION) {
    problems.push(
      `Filen har schema_version ${String(file.schema_version)}; denne importen støtter ${CMP_EXPORT_SCHEMA_VERSION}.`,
    );
  }
  if (file.destination !== undefined && file.destination !== CMP_EXPORT_DESTINATION) {
    problems.push(`Filen er eksportert for "${String(file.destination)}", ikke for Museum24.`);
  }

  const items = Array.isArray(file.items) ? file.items : [];
  if (!Array.isArray(file.items)) {
    problems.push('Filen har ingen "items"-liste.');
  } else if (items.length === 0) {
    problems.push('Filen inneholder ingen sider.');
  }

  const library = Array.isArray(file.media) ? file.media : [];
  const inspection = inspect(items, library);
  const { media } = inspection;
  const metadata = file.export ?? {};
  const dmsEnvironment = file.dms_environment ?? metadata.dms_environment;

  if (media.unresolved > 0) {
    warnings.push(
      `${media.unresolved} mediereferanse${media.unresolved === 1 ? '' : 'r'} mangler EMS-id. ` +
        'Disse sidene blir importert uten de bildene - last dem opp til EMS og eksporter på nytt hvis du vil ha dem med.',
    );
  }
  // A file written before the platform carried the library at all. Importable, but it
  // leaves the museum's other files out of the gallery - which is the problem the
  // library list exists to fix, so it is said plainly rather than left to be noticed.
  if (media.library === 0 && media.uniqueFiles > 0) {
    warnings.push(
      'Filen inneholder ingen medieliste, bare bildene sidene bruker. Resten av ' +
        'nettstedets filer i EMS blir ikke tilgjengelige i mediearkivet - eksporter ' +
        'på nytt fra plattformen hvis du vil ha dem med.',
    );
  }
  // The file states how much it should contain; a mismatch means a truncated or
  // hand-edited download, which is worth seeing before it becomes pages.
  if (typeof metadata.media_count === 'number' && metadata.media_count !== media.library) {
    warnings.push(`Filen oppgir ${metadata.media_count} mediefiler, men inneholder ${media.library}.`);
  }
  if (typeof metadata.item_count === 'number' && metadata.item_count !== items.length) {
    warnings.push(
      `Filen oppgir ${metadata.item_count} sider, men inneholder ${items.length}. ` +
        'Nedlastingen kan være ufullstendig.',
    );
  }

  return {
    pages: items.length,
    languages: inspection.languages,
    pagesByKind: inspection.pagesByKind,
    items: inspection.items,
    media,
    hierarchy: inspection.hierarchy,
    metadata,
    dmsEnvironment,
    problems,
    warnings,
  };
}
